import { QueryClient, hydrate } from '@tanstack/react-query';
import { createStore, del, get, set } from 'idb-keyval';

const MINUTE_MS = 60 * 1000;
export const SHORT_KEEP_MS = 30 * MINUTE_MS;
export const LONG_KEEP_MS = 7 * 24 * 60 * MINUTE_MS;
export const RESTORE_TIMEOUT_MS = 300;
export const WRITE_THROTTLE_MS = 1000;

const LONG_KEPT = ['landing', 'population', 'trend', 'access'].map((kind) => ['wfm', kind]);
const CACHE_KEY = 'query-cache';
const LEGACY_KEY = 'REACT_QUERY_OFFLINE_CACHE';
const OWNER_KEY = 'ee_assistant_email';
const MIXED_OWNERS = Symbol('mixed owners');
const TIMED_OUT = Symbol('timed out');

const isLongKept = (queryKey) => LONG_KEPT.some(([scope, kind]) => queryKey[0] === scope && queryKey[1] === kind);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * MINUTE_MS,
      gcTime: SHORT_KEEP_MS,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});
LONG_KEPT.forEach((queryKey) => queryClient.setQueryDefaults(queryKey, { gcTime: LONG_KEEP_MS }));

let store;
const cacheStore = () => {
  store ??= createStore('ai-ops-portal', 'query-cache');
  return store;
};

const currentOwner = () => localStorage.getItem(OWNER_KEY);

let tabOwner = null;
let pendingSave = null;
let saveTimer = null;

const warn = (action, error) => console.warn(`[query-cache] ${action} failed:`, error);

function withTimeout(promise, ms) {
  let timeoutId;
  const timeout = new Promise((resolve) => { timeoutId = setTimeout(resolve, ms, TIMED_OUT); });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

async function removeStored() {
  try {
    await del(CACHE_KEY, cacheStore());
  } catch (error) {
    warn('clear', error);
  }
}

function cloneable(query) {
  try {
    structuredClone(query);
    return true;
  } catch {
    return false;
  }
}

async function write(entry) {
  try {
    await set(CACHE_KEY, entry, cacheStore());
  } catch (error) {
    if (error?.name !== 'DataCloneError') {
      warn('write', error);
      return;
    }
    if (entry.owner !== currentOwner()) return;
    const queries = entry.client.clientState.queries.filter(cloneable);
    const client = { ...entry.client, clientState: { queries, mutations: [] } };
    try {
      await set(CACHE_KEY, { ...entry, client }, cacheStore());
    } catch (retryError) {
      warn('write', retryError);
    }
  }
}

function flush() {
  saveTimer = null;
  const entry = pendingSave;
  pendingSave = null;
  if (entry && entry.owner === currentOwner()) write(entry);
}

function usableClient(entry, owner) {
  const queries = entry.client?.clientState?.queries;
  if (!Array.isArray(queries) || entry.owner !== owner) {
    removeStored();
    return undefined;
  }
  const now = Date.now();
  const savedRecently = now - entry.client.timestamp <= SHORT_KEEP_MS;
  const kept = queries.filter((query) => (
    isLongKept(query.queryKey) ? now - query.state.dataUpdatedAt <= LONG_KEEP_MS : savedRecently
  ));
  return { ...entry.client, clientState: { ...entry.client.clientState, queries: kept } };
}

function restoreLate(reading) {
  reading.then((entry) => {
    if (entry?.owner !== currentOwner() || entry.client?.buster !== persistOptions.buster) return;
    const client = usableClient(entry, entry.owner);
    if (client) hydrate(queryClient, client.clientState);
  }).catch((error) => warn('late restore', error));
}

const persister = {
  persistClient(client) {
    const owner = currentOwner();
    if (!owner) return;
    tabOwner ??= owner;
    if (owner !== tabOwner) {
      tabOwner = MIXED_OWNERS;
      return;
    }
    pendingSave = { owner, client };
    saveTimer ??= setTimeout(flush, WRITE_THROTTLE_MS);
  },

  async restoreClient() {
    try {
      localStorage.removeItem(LEGACY_KEY);
      const reading = get(CACHE_KEY, cacheStore());
      const entry = await withTimeout(reading, RESTORE_TIMEOUT_MS);
      if (entry !== TIMED_OUT) return entry ? usableClient(entry, currentOwner()) : undefined;
      warn('restore', new Error(`timed out after ${RESTORE_TIMEOUT_MS}ms, merging the stored cache when it arrives`));
      restoreLate(reading);
      return undefined;
    } catch (error) {
      warn('restore', error);
      return undefined;
    } finally {
      tabOwner = currentOwner();
    }
  },

  removeClient: removeStored,
};

export const persistOptions = {
  persister,
  maxAge: LONG_KEEP_MS,
  buster: import.meta.env.APP_BUILD_ID ?? '',
};

export function clearPersistedQueries() {
  queryClient.clear();
  clearTimeout(saveTimer);
  saveTimer = null;
  pendingSave = null;
  tabOwner = null;
  return removeStored();
}
